import React from "react";
import { Globe, Loader2, Smartphone, Trash2, X } from "lucide-react";
import type { DynamicLinkStatus } from "src/types/dynamicLinks";
import { Input, Select } from "src/components/ui/Input";
import { DateTimePicker } from "src/components/ui/DateTimePicker";

export type EditLinkFormState = {
  slug: string;
  name: string;
  androidUrl: string;
  iosUrl: string;
  fallbackUrl: string;
  status: DynamicLinkStatus;
  expiresAtLocal: string;
};

export type EditLinkDialogProps = {
  edit: EditLinkFormState;
  setEdit: React.Dispatch<React.SetStateAction<EditLinkFormState | null>>;
  editError: string | null;
  editSaving: boolean;
  deleteBusySlug: string | null;
  labelClass: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onDelete: () => void;
};

export const EditLinkDialog: React.FC<EditLinkDialogProps> = ({
  edit,
  setEdit,
  editError,
  editSaving,
  deleteBusySlug,
  labelClass,
  onClose,
  onSubmit,
  onDelete,
}) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-link-title"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[1.75rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-6"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3
            id="edit-link-title"
            className="text-xl font-extrabold text-[#3C3C3C]"
          >
            Edit link
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-[#777777] hover:bg-[#F0F0F0]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 font-mono text-sm font-bold text-[#1CB0F6]">
          /{edit.slug}{" "}
          <span className="font-sans text-xs font-bold text-[#777777]">
            (slug can&apos;t be changed)
          </span>
        </p>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {editError && (
            <p className="rounded-xl bg-[#FFEDED] px-3 py-2 text-center text-sm font-extrabold text-[#FF4B4B]">
              {editError}
            </p>
          )}
          <label className={labelClass}>
            Friendly name
            <Input
              type="text"
              value={edit.name}
              onChange={(e) =>
                setEdit((s) => (s ? { ...s, name: e.target.value } : s))
              }
              maxLength={200}
            />
          </label>
          <label className={labelClass}>
            <span className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-[#58CC02]" aria-hidden />
              Android
            </span>
            <Input
              type="url"
              required
              value={edit.androidUrl}
              onChange={(e) =>
                setEdit((s) => (s ? { ...s, androidUrl: e.target.value } : s))
              }
            />
          </label>
          <label className={labelClass}>
            <span className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-[#1CB0F6]" aria-hidden />
              iOS
            </span>
            <Input
              type="url"
              required
              value={edit.iosUrl}
              onChange={(e) =>
                setEdit((s) => (s ? { ...s, iosUrl: e.target.value } : s))
              }
            />
          </label>
          <label className={labelClass}>
            <span className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#FFC800]" aria-hidden />
              Fallback
            </span>
            <Input
              type="url"
              required
              value={edit.fallbackUrl}
              onChange={(e) =>
                setEdit((s) => (s ? { ...s, fallbackUrl: e.target.value } : s))
              }
            />
          </label>
          <label className={labelClass}>
            Status
            <Select
              value={edit.status}
              onChange={(e) =>
                setEdit((s) =>
                  s ? { ...s, status: e.target.value as DynamicLinkStatus } : s,
                )
              }
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </Select>
          </label>
          <label className={labelClass}>
            Stops working at
            <DateTimePicker
              value={edit.expiresAtLocal}
              onChanged={({ value }) =>
                setEdit((s) => (s ? { ...s, expiresAtLocal: value } : s))
              }
              aria-label="Link expiry date and time"
            />
          </label>
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[48px] rounded-2xl border-2 border-[#E5E5E5] bg-[#F7F7F7] px-5 font-extrabold text-[#3C3C3C] shadow-[0_4px_0_#D0D0D0] active:translate-y-0.5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={editSaving || deleteBusySlug === edit.slug}
              className="min-h-[48px] rounded-2xl border-0 bg-[#58CC02] px-5 font-extrabold text-white shadow-[0_4px_0_#3F9600] disabled:opacity-60 active:translate-y-0.5"
            >
              {editSaving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>

        <div className="mt-6 border-t-2 border-[#F0F0F0] pt-5">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-wide text-[#777777]">
            Danger zone
          </p>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleteBusySlug === edit.slug || editSaving}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#FF4B4B] bg-[#FFEDED] px-5 font-extrabold text-[#C62828] shadow-[0_4px_0_#E0A0A0] hover:bg-[#FFE0E0] disabled:opacity-50 active:translate-y-0.5"
          >
            {deleteBusySlug === edit.slug ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden />
            )}
            Delete this link
          </button>
        </div>
      </div>
    </div>
  );
};
